type Labels = {
  org: string;
  course: string;
  pathway: string;
  step: string;
};

type Catalog = {
  shortCode?: string;
};

type Contact = {
  phone?: string;
  email?: string;
  feedback?: boolean;
};

type WhiteLabel = {
  name: string;
  url: string;
  logoSrc: string;
  logoSmSrc: string;
  labels: Labels;
  catalog?: Catalog;
  contact?: Contact;
};

const BaseWhiteLabel: WhiteLabel = {
  name: 'Ministry Grid',
  url: 'ministrygrid.com',
  logoSrc: '/img/mgpro_logo_alt.svg',
  logoSmSrc: '/img/mgpro_logo_sm.svg',
  labels: {
    org: 'church',
    course: 'course',
    pathway: 'pathway',
    step: 'step',
  },
  catalog: {
    shortCode: 'MG',
  },
  contact: {
    phone: '866-627-8553',
    email: 'ministrygrid@lifeway.com',
    feedback: false,
  },
};

const whiteLabels: { [key: string]: WhiteLabel } = {
  GRID: { ...BaseWhiteLabel },
  BELLEVUE: {
    ...BaseWhiteLabel,
    name: 'Bellevue Training',
    url: 'training.bellevue.org',
    logoSrc: '/img/bellevue_logo.svg',
    logoSmSrc: '/img/bellevue_logo_color.png',
    catalog: {
      shortCode: 'BT',
    },
    contact: {
      email: 'jgwyn@bellevue.org',
      feedback: true,
    },
  },
  MBCB: {
    ...BaseWhiteLabel,
    name: 'MBCB Training',
    url: 'training.mbcb.org',
    logoSrc: '/img/mbcb-logo.png',
    logoSmSrc: '/img/mbcb-logo-sm.png',
    catalog: {
      shortCode: 'MB',
    },
    contact: {
      email: 'msbaptist@mbcb.org',
      feedback: true,
    },
  },
  TRANSFORMATION: {
    ...BaseWhiteLabel,
    name: 'Transformation Church',
    url: 'hub.transformationchurch.tc',
    logoSrc: '/img/transformation_logo_black.png',
    catalog: {
      shortCode: 'TC',
    },
    contact: {
      phone: '803-756-4692',
      email: 'TCHub@transformationchurch.tc',
      feedback: true,
    },
  },
  IDLEWILD: {
    ...BaseWhiteLabel,
    name: 'Idlewild Church Training',
    url: 'training.idlewild.org',
    logoSrc: '/img/idlewildchurch_logo_color.png',
    catalog: {
      shortCode: 'IW',
    },
  },
  CONNEXUS: {
    ...BaseWhiteLabel,
    name: 'Connexus Church Training',
    url: 'training.connexuschurch.com',
    logoSrc: '/img/connexus_logo_color.png',
    catalog: {
      shortCode: 'CX',
    },
    contact: {
      email: 'jhood@connexuschurch.com',
      feedback: true,
    },
  },
  CENTRIKID: {
    ...BaseWhiteLabel,
    name: 'CentriKid Training Online',
    url: 'training.centrikid.lifeway.com',
    logoSrc: '/img/centrikid_logo.svg',
    catalog: {
      shortCode: 'CK',
    },
    contact: {
      email: 'centrikid@lifeway.com',
    },
  },
  FUGE: {
    ...BaseWhiteLabel,
    name: 'FUGE Training Online',
    url: 'training.fuge.lifeway.com',
    logoSrc: '/img/fuge_logo_color.png',
    catalog: {
      shortCode: 'FUGE',
    },
    contact: {
      email: 'fuge@lifeway.com',
    },
  },
  STUDENTLIFE: {
    ...BaseWhiteLabel,
    name: 'Student Life Camp Training Online',
    url: 'training.studentlife.lifeway.com',
    logoSrc: '/img/studentlife_logo.png',
    catalog: {
      shortCode: 'SL',
    },
    contact: {
      email: 'Rebekah@studentlife.com',
    },
  },
  SBC: {
    ...BaseWhiteLabel,
    name: 'SBC Training & Orientation Portal',
    url: 'training.sbc.net',
    logoSrc: '/img/sbc_logo.png',
    catalog: {
      shortCode: 'SBC',
    },
    contact: {
      email: 'info@sbc.net',
    },
  },
  A21: {
    ...BaseWhiteLabel,
    name: 'A21 Training Center',
    logoSrc: '/img/a21-logo-color.png',
    catalog: {
      shortCode: 'A21',
    },
    contact: {
      email: 'info@a21.org',
      feedback: true,
    },
  },
};

export const getWhiteLabel = (key: keyof typeof whiteLabels): WhiteLabel => {
  const whiteLabel = whiteLabels[key];
  if (!whiteLabel) {
    throw new Error(`WhiteLabel with key ${key} not found`);
  }
  return whiteLabel;
};

class WhiteLabelClass {
  private whiteLabel: WhiteLabel;

  constructor(key: keyof typeof whiteLabels) {
    this.whiteLabel = getWhiteLabel(key);
  }

  setWhiteLabel(key: keyof typeof whiteLabels) {
    this.whiteLabel = getWhiteLabel(key);
  }

  isGrid() {
    return this.whiteLabel.name === whiteLabels.GRID.name;
  }

  get name() {
    return this.whiteLabel.name;
  }
  get url() {
    return this.whiteLabel.url;
  }
  get logoSrc() {
    return this.whiteLabel.logoSrc;
  }
  get logoSmSrc() {
    return this.whiteLabel.logoSmSrc;
  }
  get labels() {
    return this.whiteLabel.labels;
  }
  get catalog() {
    return this.whiteLabel.catalog;
  }
  get contact() {
    return this.whiteLabel.contact;
  }
}

const WhiteLabel = new WhiteLabelClass(window.getOrigin ? window.getOrigin() : 'GRID');
export default WhiteLabel;
